import {
  Alert,
  Badge,
  Button,
  Code,
  CopyButton,
  Group,
  List,
  Modal,
  PasswordInput,
  SimpleGrid,
  Stack,
  Text,
} from '@mantine/core';
import { IconCheck, IconCopy, IconPlugConnected } from '@tabler/icons-react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api, izvedi as izvediKlic, podatki, type Shema } from '../api/odjemalec';
import { NapakaApi, sporocilo } from '../api/napaka';
import { Kartica, Podatki, StanjeNalaganja } from '../postavitev/Podatki';
import { cas } from './skrbnistvo';
import { glave, obNapaki } from './zeton';

type Preverjanje = Shema['StripePreverjanje'];

const KLJUC = ['skrbnik', 'stripe'];

/**
 * Skrbništvo → Stripe: povezava za plačila naročnine (bančna nakazila). Ključa se vpišeta tu in ostaneta šifrirana
 * na strežniku, ki ju pred shranjevanjem preizkusi pri Stripu; nazaj se pokaže samo začetek in konec.
 */
export function StripeSkrbnistvo() {
  const odjemalec = useQueryClient();
  const povezava = useQuery({
    queryKey: KLJUC,
    queryFn: async ({ signal }) => {
      try {
        return await podatki(api.GET('/api/skrbnik/stripe', { signal, headers: glave() }));
      } catch (e) {
        obNapaki(e);
        throw e;
      }
    },
  });
  const [apiKljuc, setApiKljuc] = useState('');
  const [webhook, setWebhook] = useState('');
  const [napake, setNapake] = useState<Record<string, string>>({});
  const [napaka, setNapaka] = useState<string | null>(null);
  const [dela, setDela] = useState<'shrani' | 'preveri' | 'odklopi' | null>(null);
  const [preverjanje, setPreverjanje] = useState<Preverjanje | null>(null);
  const [odklop, setOdklop] = useState(false);

  const izvedi = async (kaj: 'shrani' | 'preveri' | 'odklopi', klic: () => Promise<void>) => {
    setDela(kaj);
    setNapake({});
    setNapaka(null);
    try {
      await klic();
    } catch (e) {
      obNapaki(e);
      if (e instanceof NapakaApi && Object.keys(e.polja).length > 0) {
        setNapake(e.polja);
      } else {
        setNapaka(sporocilo(e));
      }
    } finally {
      setDela(null);
    }
  };

  const shrani = () =>
    izvedi('shrani', async () => {
      const p = await podatki(
        api.PUT('/api/skrbnik/stripe', { headers: glave(), body: { apiKljuc, webhookSkrivnost: webhook } }),
      );
      odjemalec.setQueryData(KLJUC, p);
      setApiKljuc('');
      setWebhook('');
      setPreverjanje(null);
    });

  const preveri = () =>
    izvedi('preveri', async () => {
      setPreverjanje(await podatki(api.POST('/api/skrbnik/stripe/preveri', { headers: glave() })));
    });

  const odklopi = () =>
    izvedi('odklopi', async () => {
      await izvediKlic(api.DELETE('/api/skrbnik/stripe', { headers: glave() }));
      setOdklop(false);
      setPreverjanje(null);
      await odjemalec.invalidateQueries({ queryKey: KLJUC });
    });

  if (!povezava.data) {
    return (
      <Kartica>
        <StanjeNalaganja
          napaka={povezava.error}
          ponovi={() => void povezava.refetch()}
          naslov="Povezave s Stripom ni bilo mogoče naložiti"
        />
      </Kartica>
    );
  }
  const p = povezava.data;
  const webhookUrl = `${window.location.origin}/api/stripe/webhook`;
  // lokalni razvoj: Stripe localhosta ne doseže, dogodke posreduje Stripe CLI (scripts/stripe-lokalno.sh)
  const lokalno = ['localhost', '127.0.0.1'].includes(window.location.hostname);

  return (
    <SimpleGrid cols={{ base: 1, lg: 2 }} spacing="md">
      <Kartica naslov="Povezava" dejanja={<StanjePovezave p={p} />}>
        <Stack gap="sm">
          <Podatki
            vrstice={[
              ['Ključ (API)', p.apiKljuc ? <Code>{p.apiKljuc}</Code> : 'ni vpisan'],
              ['Skrivnost za webhook', p.webhookSkrivnost ? <Code>{p.webhookSkrivnost}</Code> : 'ni vpisana'],
              ['Nazadnje spremenil', p.spremenil ? `${p.spremenil}, ${cas(p.spremenjeno)}` : ''],
            ]}
          />
          {preverjanje && <RezultatPreverjanja r={preverjanje} />}
          <PasswordInput
            label="Omejeni ključ (API)"
            description={
              p.apiKljuc ? 'Vpišite samo, če ga želite zamenjati.' : 'rk_live_… ali rk_test_… (Developers → API keys)'
            }
            placeholder={p.apiKljuc ?? 'rk_live_…'}
            value={apiKljuc}
            onChange={(e) => setApiKljuc(e.currentTarget.value)}
            error={napake.apiKljuc}
            autoComplete="off"
          />
          <PasswordInput
            label="Skrivnost za webhook"
            description={
              p.webhookSkrivnost
                ? 'Vpišite samo, če jo želite zamenjati.'
                : 'whsec_… (Developers → Webhooks → Signing secret)'
            }
            placeholder={p.webhookSkrivnost ?? 'whsec_…'}
            value={webhook}
            onChange={(e) => setWebhook(e.currentTarget.value)}
            error={napake.webhookSkrivnost}
            autoComplete="off"
          />
          {napaka && (
            <Alert color="red" variant="light" role="alert">
              {napaka}
            </Alert>
          )}
          <Group gap="xs">
            <Button
              onClick={() => void shrani()}
              loading={dela === 'shrani'}
              disabled={dela !== null && dela !== 'shrani'}
            >
              Shrani
            </Button>
            <Button
              variant="default"
              leftSection={<IconPlugConnected size={18} />}
              onClick={() => void preveri()}
              loading={dela === 'preveri'}
              disabled={!p.apiKljuc || (dela !== null && dela !== 'preveri')}
            >
              Preveri povezavo
            </Button>
            {(p.apiKljuc || p.webhookSkrivnost) && (
              <Button variant="subtle" color="red" onClick={() => setOdklop(true)} disabled={dela !== null}>
                Odklopi
              </Button>
            )}
          </Group>
        </Stack>
      </Kartica>

      <Kartica naslov="Nastavitev v Stripu">
        <Stack gap="sm">
          <Text size="sm">
            Vsako okolje ima svojo povezavo: na razvojnem strežniku testni ključ (rk_test_…), na produkciji pravi
            (rk_live_…).
          </Text>
          <List type="ordered" spacing="xs" size="sm">
            <List.Item>
              <b>Settings → Payments → Payment methods</b>: vklopite <b>Bank transfers</b> (bančna nakazila v EUR).
            </List.Item>
            <List.Item>
              <b>Developers → API keys → Create restricted key</b> s pravicami <b>Customers: Write</b> in{' '}
              <b>PaymentIntents: Write</b> (ostalo None). Ključ prilepite levo.
            </List.Item>
            {lokalno ? (
              <List.Item>
                Stripe lokalnega naslova ne doseže, zato webhooka v Stripu ne dodajate. Zaženite Stripe CLI (enkrat prej{' '}
                <Code>stripe login</Code>):
                <Kopiraj vrednost="scripts/stripe-lokalno.sh" />
                Skripta dogodke posreduje lokalnemu strežniku in izpiše skrivnost (whsec_…) – prilepite jo levo, nato{' '}
                <b>Shrani</b> in <b>Preveri povezavo</b>.
              </List.Item>
            ) : (
              <>
                <List.Item>
                  <b>Developers → Webhooks → Add destination</b> z naslovom
                  <Kopiraj vrednost={webhookUrl} />
                  in dogodki
                  <Stack gap={4} mt={4}>
                    {(p.dogodki ?? []).map((d) => (
                      <Kopiraj key={d} vrednost={d} />
                    ))}
                  </Stack>
                </List.Item>
                <List.Item>
                  Pri webhooku kopirajte <b>Signing secret</b> (whsec_…) in ga prilepite levo, nato <b>Shrani</b> in{' '}
                  <b>Preveri povezavo</b>.
                </List.Item>
              </>
            )}
          </List>
          <Alert color="blue" variant="light">
            Preizkus: pri instanci (kartica Naročnina in točke) <b>Pripravi IBAN</b>, nato v testnem načinu{' '}
            <b>Simuliraj nakazilo</b> – točke (in bonus) se pripišejo, ko Stripe pošlje webhook. Če Stripe zavrne klic
            zaradi pravic, sporočilo pove, katero pravico ključu dodati.
          </Alert>
        </Stack>
      </Kartica>

      <Modal opened={odklop} onClose={() => setOdklop(false)} title="Odklopi Stripe" radius="lg">
        <Stack gap="sm">
          <Text size="sm">
            Ključ in skrivnost za webhook se izbrišeta. Dokler ju znova ne vpišete, stranke ne morejo plačevati, prilivi
            pa ostanejo pri Stripu.
          </Text>
          <Group justify="flex-end" gap="xs">
            <Button variant="subtle" onClick={() => setOdklop(false)}>
              Prekliči
            </Button>
            <Button color="red" onClick={() => void odklopi()} loading={dela === 'odklopi'}>
              Odklopi
            </Button>
          </Group>
        </Stack>
      </Modal>
    </SimpleGrid>
  );
}

function StanjePovezave({ p }: { p: Shema['StripePovezava'] }) {
  return (
    <Group gap={4} wrap="nowrap">
      {p.nacin === 'test' && (
        <Badge color="yellow" variant="light">
          testni način
        </Badge>
      )}
      {p.povezano ? (
        <Badge color="green" variant="light">
          povezano
        </Badge>
      ) : (
        <Badge color="gray" variant="light">
          ni povezano
        </Badge>
      )}
    </Group>
  );
}

const BANCNA_NAKAZILA: Record<string, string> = {
  active: 'vklopljena',
  pending: 'v preverjanju',
  inactive: 'niso vklopljena',
};

function RezultatPreverjanja({ r }: { r: Preverjanje }) {
  if (!r.uspelo) {
    return (
      <Alert color="red" variant="light" role="alert" title="Povezava ne deluje">
        {r.sporocilo}
      </Alert>
    );
  }
  return (
    <Alert color="green" variant="light" icon={<IconCheck size={18} />} title="Povezava deluje">
      <Text size="sm">
        {r.racun ? `Račun ${r.racun}` : 'Račun'}
        {r.racunId ? ` (${r.racunId}${r.drzava ? `, ${r.drzava}` : ''})` : ''},{' '}
        {r.nacin === 'live' ? 'pravi način' : 'testni način'}.
      </Text>
      {r.bancnaNakazila ? (
        <Text size="sm" c={r.bancnaNakazila === 'active' ? undefined : 'red'}>
          Bančna nakazila: {BANCNA_NAKAZILA[r.bancnaNakazila] ?? r.bancnaNakazila}
        </Text>
      ) : (
        <Text size="sm" c="dimmed">
          Ključ ne sme brati podatkov računa – ali so bančna nakazila vklopljena, preverite v Stripu.
        </Text>
      )}
    </Alert>
  );
}

function Kopiraj({ vrednost }: { vrednost: string }) {
  return (
    <Group gap="xs" wrap="nowrap" my={4}>
      <Code style={{ overflowWrap: 'anywhere', minWidth: 0 }}>{vrednost}</Code>
      <CopyButton value={vrednost}>
        {({ copied, copy }) => (
          <Button
            size="compact-xs"
            variant="subtle"
            style={{ flexShrink: 0 }}
            leftSection={<IconCopy size={12} />}
            onClick={copy}
            aria-label={`Kopiraj ${vrednost}`}
          >
            {copied ? 'Kopirano' : 'Kopiraj'}
          </Button>
        )}
      </CopyButton>
    </Group>
  );
}
